import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Card, Layout, PageHeader, Spin, Typography, Space, List, Tag } from 'antd';
import { apiClient, apiErrorMessage } from '../../utils';
import { ReleaseNote, ReleaseNotesResponse } from '../../models';
import { UserContext } from '../contexts';
import { hasPermission } from '../../utils/user-roles-helper';

const months: { [key: string]: string } = {
  Jan: 'Ocak',
  Feb: 'Şubat',
  Mar: 'Mart',
  Apr: 'Nisan',
  May: 'Mayıs',
  Jun: 'Haziran',
  Jul: 'Temmuz',
  Aug: 'Ağustos',
  Sep: 'Eylül',
  Oct: 'Ekim',
  Nov: 'Kasım',
  Dec: 'Aralık',
};

const formatDate = (dateStr: string): string => {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;
  const day = parts[0];
  const month = months[parts[1]];
  const year = '20' + parts[2];
  return `${day} ${month} ${year}`;
};

export function ReleaseNotesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showIssueNo, setShowIssueNo] = useState(false);

  useEffect(() => {
    setShowIssueNo(hasPermission(user, 'releaseNotes.issue_no_view'));
  }, [user]);

  const loadReleaseNotes = useCallback((currentPage: number) => {
    setLoading(true);
    apiClient
      .get(`help/release-notes?page=${currentPage}`)
      .then(response => {
        setLoading(false);
        const data = response.data as ReleaseNotesResponse;
        if (data.releaseNotes.length > 0) {
          setReleaseNotes(prev => [...prev, ...data.releaseNotes]);
          setPage(currentPage);
        } else {
          setHasMore(false);
        }
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadReleaseNotes(1);
  }, [loadReleaseNotes]);

  const onLoadMore = () => {
    loadReleaseNotes(page + 1);
  };

  const renderIssues = (issues: { issuekey: string; summary: string }[], showIssueNo: boolean) => {
    return (
      <List
        size="small"
        dataSource={issues}
        renderItem={(issue) => (
          <List.Item style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            {showIssueNo ? (
              <>
                <Tag color="blue">{issue.issuekey}</Tag>
                <span style={{ marginLeft: 8 }}>{issue.summary}</span>
              </>
            ) : (
              <span>{issue.summary}</span>
            )}
          </List.Item>
        )}
      />
    );
  };

  const renderSection = (
    section: { title: string; jqlsection: { results: { issuekey: string; summary: string }[] } },
    showIssueNo: boolean,
  ) => {
    if (section.jqlsection.results.length === 0) {
      return null;
    }
    return (
      <div>
        <Text strong>{section.title}</Text>
        {renderIssues(section.jqlsection.results, showIssueNo)}
      </div>
    );
  };

  return (
    <>
      <PageHeader title="Sürüm notları" />
      <Content style={{ padding: 24 }}>
        <Space direction="vertical" size="large">
          {releaseNotes.map((note, index) => (
            <Card key={index} title={note.versionName} extra={formatDate(note.releaseDate)}>
              <Space direction="vertical" size="middle">
                {renderSection(note.stories, showIssueNo)}
                {renderSection(note.bugs, showIssueNo)}
              </Space>
            </Card>
          ))}
          {hasMore && (
            <div style={{ textAlign: 'center' }}>
              <Button onClick={onLoadMore} loading={loading} disabled={loading}>
                Daha fazla
              </Button>
            </div>
          )}
          {loading && releaseNotes.length === 0 && <Spin size="large" />}
        </Space>
      </Content>
    </>
  );
}
